import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../context/AuthContext';
import supabase from '../../lib/supabase';

const NavBar = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState('');
  const [avatarIcon, setAvatarIcon] = useState('FiUser');
  const [loading, setLoading] = useState(false);

  // Load user's profile data when component mounts
  useEffect(() => {
    if (user) {
      fetchUserProfile();
    }
  }, [user]);

  // Function to fetch user's profile data
  const fetchUserProfile = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles_sb2847x9')
        .select('username, avatar_icon')
        .eq('id', user.id)
        .single();

      if (!error && data) {
        setDisplayName(data.username || user.email?.split('@')[0] || '');
        setAvatarIcon(data.avatar_icon || 'FiUser');
      } else {
        // Fall back to email username if no profile found
        setDisplayName(user.email?.split('@')[0] || '');
        setAvatarIcon('FiUser');
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      // Fall back to email username
      setDisplayName(user.email?.split('@')[0] || '');
      setAvatarIcon('FiUser');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  // Get the icon component from the avatar icon ID
  const getAvatarIcon = () => {
    const iconMap = {
      FiUser: FiIcons.FiUser,
      FiSmile: FiIcons.FiSmile,
      FiHeart: FiIcons.FiHeart,
      FiStar: FiIcons.FiStar,
      FiSun: FiIcons.FiSun,
      FiMoon: FiIcons.FiMoon,
      FiZap: FiIcons.FiZap,
      FiFeather: FiIcons.FiFeather,
      FiMusic: FiIcons.FiMusic,
      FiCamera: FiIcons.FiCamera,
      FiBook: FiIcons.FiBook,
      FiGlobe: FiIcons.FiGlobe,
      FiCompass: FiIcons.FiCompass,
      FiAnchor: FiIcons.FiAnchor,
      FiGift: FiIcons.FiGift,
      FiCoffee: FiIcons.FiCoffee,
      FiUmbrella: FiIcons.FiUmbrella,
      FiHeadphones: FiIcons.FiHeadphones,
      FiPenTool: FiIcons.FiPenTool
    };
    
    return iconMap[avatarIcon] || FiIcons.FiUser;
  };

  return (
    <nav className="bg-white shadow-soft mb-6 py-3 px-4 rounded-xl">
      <div className="max-w-6xl mx-auto flex justify-between items-center">
        <Link to="/" className="flex items-center gap-2">
          <motion.div
            whileHover={{ rotate: 10 }}
            className="bg-gradient-to-br from-primary-400 to-purple-600 text-white p-2 rounded-lg"
          >
            <SafeIcon icon={FiIcons.FiBook} className="text-xl" />
          </motion.div>
          <span className="text-xl font-display font-bold text-primary-600">
            Write Your Pathway
          </span>
        </Link>

        <div className="flex items-center gap-4">
          {user ? (
            <>
              <div className="flex items-center gap-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleProfileClick}
                  className="p-2 rounded-full bg-purple-100 hover:bg-purple-200 text-purple-700"
                  title="Edit profile"
                >
                  <SafeIcon icon={getAvatarIcon()} className="text-lg" />
                </motion.button>
                <span className="text-purple-600 font-medium">
                  {loading ? '...' : displayName}
                </span>
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-purple-100 hover:bg-purple-200 text-purple-700 px-3 py-1 rounded-lg font-medium text-sm flex items-center gap-1"
                onClick={handleSignOut}
              >
                <SafeIcon icon={FiIcons.FiLogOut} className="text-sm" />
                Sign Out
              </motion.button>
            </>
          ) : (
            <>
              <Link to="/login">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-purple-100 hover:bg-purple-200 text-purple-700 px-3 py-1 rounded-lg font-medium text-sm flex items-center gap-1"
                >
                  <SafeIcon icon={FiIcons.FiLogIn} className="text-sm" />
                  Sign In
                </motion.button>
              </Link>
              <Link to="/signup">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-primary-500 hover:bg-primary-600 text-white px-3 py-1 rounded-lg font-medium text-sm flex items-center gap-1"
                >
                  <SafeIcon icon={FiIcons.FiUserPlus} className="text-sm" />
                  Sign Up
                </motion.button>
              </Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default NavBar;